import React, { useEffect, useMemo, useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { LoaderCircle, Eye, MoreHorizontal, CheckCircle, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { SellerInfo, StylistInfo, UserInfo } from "@/types/AppTypes";
import { UsersDisplayTable } from "@/components/superAdminDashboardSidebar";
import { UserDetailsModal } from "@/components";

import { useInfiniteQuery } from "@tanstack/react-query";
import { createUserWithBuyerInfosQueryOptions } from "@/queryOptions/combined/userWithBuyerInfos/createUserWithBuyerInfosQueryOptions";
import { ApiPagination } from "@/components";
import { createUserWithSellerInfosQueryOptions } from "@/queryOptions/combined/userWithSellerInfos/createUserWithSellerInfosQueryOptions";


type UserWithRoleInfo = (UserInfo & {role?:string} & StylistInfo) | (UserInfo & {role?:string} & SellerInfo);

const ApprovedUsers = () => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedUser, setSelectedUser] = useState<UserWithRoleInfo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: buyerData,
    fetchNextPage: buyerFetchNextPage,
    hasNextPage: buyerHasNextPage,
    isFetchingNextPage: buyerIsFetchingNextPage,
    isLoading: buyerIsLoading,
    isSuccess: buyerIsSuccess,
  } = useInfiniteQuery(
    createUserWithBuyerInfosQueryOptions(
      {
        is_verified: { eq: true },
      },
      5,
      {
        staleTime: 1000 * 60 * 5,
      }
    )
  );

  const {
    data: sellerData,
    fetchNextPage: sellerFetchNextPage,
    hasNextPage: sellerHasNextPage,
    isFetchingNextPage: sellerIsFetchingNextPage,
    isLoading: sellerIsLoading,
    isSuccess: sellerIsSuccess,
  } = useInfiniteQuery(
    createUserWithSellerInfosQueryOptions(
      {
        is_verified: { eq: true },
      },
      5,
      {
        staleTime: 1000 * 60 * 5,
      }
    )
  );

  
  useEffect(() => {
    if (buyerIsSuccess && buyerData?.pages?.length === 1 && buyerHasNextPage) {
      buyerFetchNextPage();
    }
  }, [buyerIsSuccess, buyerData?.pages?.length, buyerHasNextPage, buyerFetchNextPage]);

  useEffect(() => {
    if (sellerIsSuccess && sellerData?.pages?.length === 1 && sellerHasNextPage) {
      sellerFetchNextPage();
    }
  }, [sellerIsSuccess, sellerData?.pages?.length, sellerHasNextPage, sellerFetchNextPage]);

  
  const combinedCurrentPageData = useMemo(() => {

    const buyerPage = buyerData?.pages?.[currentPageIndex];
    const sellerPage = sellerData?.pages?.[currentPageIndex];

    const buyersFromCurrentPage = buyerPage?.combinedUsers || [];
    const sellersFromCurrentPage = sellerPage?.combinedUsers || [];

    return [...buyersFromCurrentPage, ...sellersFromCurrentPage];
  }, [buyerData, sellerData, currentPageIndex]);

  const totalPages = useMemo(() => {
    const buyerPages = buyerData?.pages?.length || 0;
    const sellerPages = sellerData?.pages?.length || 0;
  
  
    let maxPages = 0;
  
    for (let i = 0; i < Math.max(buyerPages, sellerPages); i++) {
      const hasBuyer = (buyerData?.pages?.[i]?.combinedUsers?.length ?? 0) > 0;
      const hasSeller = (sellerData?.pages?.[i]?.combinedUsers?.length ?? 0) > 0;
  
      if (hasBuyer || hasSeller) {
        maxPages++;
      } else {
        break; 
      }
    }
  
    return maxPages;
  }, [buyerData, sellerData]);

 
  const hasNextPageToShow = useMemo(() => {
    const nextPageIndex = currentPageIndex + 1;
  
    const buyerPageExists = (buyerData?.pages?.[nextPageIndex]?.combinedUsers?.length ?? 0) > 0;
    const sellerPageExists = (sellerData?.pages?.[nextPageIndex]?.combinedUsers?.length ?? 0) > 0;
  
    const canLoadMoreBuyer = buyerHasNextPage && !buyerPageExists;
    const canLoadMoreSeller = sellerHasNextPage && !sellerPageExists;
  
    return buyerPageExists || sellerPageExists || canLoadMoreBuyer || canLoadMoreSeller;
  }, [
    currentPageIndex,
    buyerData?.pages,
    sellerData?.pages,
    buyerHasNextPage,
    sellerHasNextPage,
  ]);

  const hasMorePages = buyerHasNextPage || sellerHasNextPage;

 
  const isCurrentlyFetching = buyerIsFetchingNextPage || sellerIsFetchingNextPage;

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const handleNext = () => {
    const nextPageIndex = currentPageIndex + 1;
  
    if (!hasNextPageToShow) return;
  
    setCurrentPageIndex(nextPageIndex);
  
    const shouldFetchBuyerNext =
      !buyerData?.pages?.[nextPageIndex + 1] && buyerHasNextPage;
  
    const shouldFetchSellerNext =
      !sellerData?.pages?.[nextPageIndex + 1] && sellerHasNextPage;
  
    if (shouldFetchBuyerNext) {
      buyerFetchNextPage();
    }
  
    if (shouldFetchSellerNext) {
      sellerFetchNextPage();
    }
  };

  const handleAddClient = () => {
    console.log("Add new client");
  };

  const handleViewDetails = (user: UserWithRoleInfo) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };


  if (buyerIsLoading || sellerIsLoading) {
    return (
      <div className="flex items-center justify-center w-full h-[75vh]">
        <LoaderCircle className="w-6 h-6 animate-spin mr-4"></LoaderCircle>
        Fetching Users details. This won't take long!
      </div>
    );
  }

  return (
    <div className="container py-12 px-0 md:max-w-[90rem] h-full">
      <UsersDisplayTable
        users={combinedCurrentPageData}
        columns={getColumns({ onViewDetails: handleViewDetails })}
        onAddClient={handleAddClient}
        children={
          <ApiPagination
            currentPageIndex={currentPageIndex}
            totalPages={totalPages}
            hasNextPage={hasNextPageToShow}
            isFetching={isCurrentlyFetching}
            onPrevious={handlePrevious}
            onNext={handleNext}
            variant="minimal"
          />
        }
      />

      <UserDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        user={selectedUser}
        showApprovalActions={false}
      />
    </div>
  );
};

export default ApprovedUsers;

function getColumns({ 
  onViewDetails 
}: {
  onViewDetails: (user: UserWithRoleInfo) => void;
}): ColumnDef<UserWithRoleInfo>[] {
  return [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex flex-col py-1">
            <span className="font-medium">
              {user?.first_name} {user?.last_name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <span className="text-sm">{row.original?.email || "N/A"}</span>,
    },
    {
      accessorKey: "phone",
      header: "Phone Number",
      cell: ({ row }) => <span className="text-sm">{row.original?.phone || "(212) 555-1234"}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const isActive = row.original?.is_verified ?? true;
        return (
          <Badge
            variant={isActive ? "default" : "secondary"}
            className={isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
          >
            {isActive ? "Verified" : "Unverified"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "user_type",
      header: "User Type",
      cell: ({ row }) => {
        const user = row.original;
        const isSeller = user?.role === "Distributor";
        const isBuyer = user?.role === "Stylist";

        let clientType = "Stylist";
        let badgeColor = "bg-blue-100 text-blue-800";

        if (isSeller) {
          clientType = "Distributor";
          badgeColor = "bg-yellow-100 text-yellow-800";
        } else if (isBuyer) {
          clientType = "Stylist";
          badgeColor = "bg-blue-100 text-blue-800";
        }

        return (
          <Badge variant="outline" className={badgeColor}>
            {clientType}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      enableHiding: false,
      header: () => <div className="text-center">Actions</div>,
      cell: ({ row }) => {
        const user = row.original;

        return (
          <div className="flex w-full items-center justify-center">
            <Button 
              variant="ghost" 
              onClick={() => onViewDetails(user)}
              className="flex items-center"
            >
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </Button>
          </div>
        );
      },
    },
  ];
}
